import React, { ChangeEvent, FC, ReactChild, useEffect, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import { useDispatch, useSelector } from 'react-redux'
import { reportTrackingEvent } from '../../tracking/thunks'
import { TextInput } from '../styled/TextInput/TextInput'
import { ReduxState } from '../../store/reducers'

export interface Validation {
  regExp?: RegExp
  minLength?: number
  validationFunction?: { (edit: string): boolean }
  validationPromise?: { (edit: string): Promise<{ valid: boolean; formatted?: string }> }
}

export interface Transformer {
  sourceToEdit: { (source: string): string }
  editToSource: { (edit: string): string }
}

export interface Filter {
  filterFn: { (edit: string): string }
}

export interface VTextInputResponse {
  valid: boolean
  value?: string
}

export interface ReportingData {
  action?: string
  label: string
  pageTitle: string
  inputValue: string
  eventName: string
}

type VTextInputProps = {
  label: ReactChild
  type?: string
  name?: string
  id: string
  testId?: string
  maxLength?: number
  throttleDuration?: number
  placeholder: string
  errorMessage: string
  initialValue: string
  validation?: Validation
  transformer?: Transformer
  filter?: Filter
  onChange(response: VTextInputResponse): void
  reportingData?: ReportingData
  affixRight?: 'none' | 'unit' | 'icon'
  unitRight?: 'none' | 'percent' | 'sqm' | 'eur' | 'eurPerSqm' | 'years' | 'months'
  validateOnMount?: boolean
  className?: string
}

const VTextInput: FC<VTextInputProps> = ({
  initialValue,
  id,
  testId,
  name,
  type,
  label,
  throttleDuration,
  unitRight,
  affixRight,
  placeholder,
  maxLength,
  errorMessage,
  validation,
  transformer,
  filter,
  onChange,
  reportingData,
  validateOnMount = true,
  className,
}: VTextInputProps) => {
  const theme = useSelector((state: ReduxState) => state.config.theme)
  const longThrottleDuration = throttleDuration || 2000
  const shortThrottleDuration = 10
  const errorMessageDelay = validation && validation.validationPromise ? shortThrottleDuration : longThrottleDuration
  const validationDelay = validation && validation.validationPromise ? longThrottleDuration : shortThrottleDuration

  const throttledErrorMessage = useRef(debounce((valid) => setValidDelayed(valid), errorMessageDelay)).current
  const throttledValidation = useRef(
    debounce((editString, firstTimeCheck) => {
      if (firstTimeCheck) {
        return
      }
      checkEditString(editString)
    }, validationDelay)
  ).current

  const transFormToEdit = (source: string) => {
    if (transformer) {
      return transformer.sourceToEdit(source)
    }
    return source
  }

  const [editString, setEditString] = useState<string>(transFormToEdit(initialValue))
  const [invalidWasReported, setInvalidWasReported] = useState<boolean>(false)
  const [validDelayed, setValidDelayed] = useState<boolean>(true)
  const [valid, setValid] = useState<boolean>(true)
  const [firstTimeCheck, setFirstTimeCheck] = useState<boolean>(!validateOnMount)

  let containerClassSpanIndex = 3
  if (unitRight && affixRight) {
    containerClassSpanIndex++
  }
  if (label) {
    containerClassSpanIndex++
  }

  const dispatch = useDispatch()

  useEffect(() => {
    throttledValidation.cancel()
    throttledValidation(editString, firstTimeCheck)
  }, [editString, throttledValidation, firstTimeCheck])

  useEffect(() => {
    throttledErrorMessage.cancel()
    valid ? setValidDelayed(valid) : throttledErrorMessage(valid)
  }, [editString, valid, throttledErrorMessage])

  useEffect(() => {
    setEditString(initialValue)
  }, [initialValue])

  useEffect(() => {
    if (invalidWasReported) {
      return
    }

    if (validDelayed) {
      return
    }

    if (!reportingData) {
      return
    }

    const { pageTitle, label, action, inputValue, eventName } = reportingData
    dispatch(reportTrackingEvent({ pageTitle, label, action, inputValue, eventName }))

    setInvalidWasReported(true)
  }, [validDelayed, invalidWasReported, reportingData])

  const transFormToSource = (editText: string) => {
    if (transformer) {
      return transformer.editToSource(editText)
    }
    return editText
  }

  const validate = (inputValue: string) => {
    return new Promise<{ valid: boolean; formatted?: string }>((resolve, reject) => {
      if (!validation) {
        return resolve({
          valid: true,
          formatted: inputValue,
        })
      }

      if (validation.minLength !== undefined) {
        if (inputValue.length < validation.minLength) {
          return resolve({
            valid: false,
          })
        }
      }
      if (validation.regExp !== undefined) {
        if (!inputValue.match(validation.regExp)) {
          return resolve({
            valid: false,
          })
        }
      }
      if (validation.validationFunction !== undefined) {
        const result = validation.validationFunction(inputValue)
        if (!result) {
          return resolve({
            valid: false,
          })
        }
      }
      if (validation.validationPromise !== undefined) {
        return validation.validationPromise(inputValue).then((result) => {
          return resolve(result)
        })
      }

      return resolve({
        valid: true,
        formatted: inputValue,
      })
    })
  }

  const checkEditString = (editString: string) => {
    validate(editString)
      .then((result) => {
        if (result.valid) {
          setValid(true)
          if (result.formatted !== undefined) {
            setEditString(result.formatted)
          }
          onChange({
            valid: true,
            value: transFormToSource(result.formatted || editString),
          })
        } else {
          setValid(false)
        }
      })
      .catch(() => {
        setValid(false)
      })
  }

  const onChangeFunction = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({
      valid: false,
    })
    const newEditString = filter ? filter.filterFn(event.target.value) : event.target.value
    setEditString(newEditString)
    setFirstTimeCheck(false)
  }

  return (
    <div className={`fade-in-error-message-${containerClassSpanIndex}`}>
      <TextInput
        name={name}
        affixRight={affixRight}
        unitRight={unitRight}
        label={label}
        id={id}
        data-testid={testId}
        type={type}
        placeholder={placeholder}
        maxLength={maxLength}
        errorMessage={errorMessage}
        valid={validDelayed}
        value={editString}
        onChange={onChangeFunction}
        textColor={theme?.text}
        borderColor={theme?.secondary}
        backgroundColor={theme?.background}
        className={className}
      />
    </div>
  )
}

export default VTextInput
